import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { API_BASE_URL } from '@/environments/environment';
import { ProductSpecialHandlingDto } from 'sham-components/shared/models';

const BASE_URL = `${API_BASE_URL}/api/v1/seller/catalog/products`;

@Injectable({
  providedIn: 'root',
})
export class SellerProductsApi {
  private readonly http = inject(HttpClient);

  listProducts(
    query: ListSellerProductsQuery,
  ): Observable<PagedResult<SellerProductListItemDto>> {
    const page = query.page ?? 1;
    const pageSize = query.pageSize ?? 20;

    let params = new HttpParams()
      .set('page', String(page))
      .set('pageSize', String(pageSize))
      .set('sellerId', window.localStorage.getItem('seller_id')!);

    if (query.search) params = params.set('search', query.search);
    if (query.categoryId) params = params.set('categoryId', query.categoryId);
    return this.http.get<PagedResult<SellerProductListItemDto>>(BASE_URL, {
      params,
    });
  }

  getProductDetails(productId: string): Observable<SellerProductDetailsDto> {
    let params = new HttpParams().set(
      'sellerId',
      window.localStorage.getItem('seller_id')!,
    );
    return this.http.get<SellerProductDetailsDto>(`${BASE_URL}/${productId}/full`, {
      params,
    });
  }

  createProduct(cmd: any): Observable<string> {
    return this.http.post<string>(BASE_URL, cmd);
  }

  updateProduct(productId: string, cmd: any): Observable<void> {
    return this.http.put<void>(`${BASE_URL}/${productId}`, cmd);
  }

  deleteProduct(productId: string, sellerId: string): Observable<void> {
    return this.http.put<void>(`${BASE_URL}/${productId}/delete`, null, {
      params: { sellerId },
    });
  }

  publishProduct(productId: string, sellerId: string): Observable<void> {
    return this.http.put<void>(`${BASE_URL}/${productId}/publish`, null, {
      params: { sellerId },
    });
  }

  archiveProduct(productId: string, sellerId: string): Observable<void> {
    return this.http.put<void>(`${BASE_URL}/${productId}/archive`, null, {
      params: { sellerId },
    });
  }

  submitProduct(formData: FormData): Observable<void> {
    return this.http.post<void>(`${BASE_URL}/submit`, formData);
  }
}

export interface ListSellerProductsQuery {
  search?: string;
  categoryId?: string;
  page?: number;
  pageSize?: number;
}

export interface PagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface SellerProductListItemDto {
  id: string;
  categoryId: string;
  brandId?: string;
  productType: string;
  status: string;
  name: string;
  thumbnailUrl?: string;
  createdAtUtc: string;
  activeDraft?: ProductActiveDraftSummaryDto | null;
}

export interface ProductActiveDraftSummaryDto {
  productDraftId: string;
  status: string;
  createdAtUtc: string;
  updatedAtUtc: string;
  submittedAtUtc?: string;
  reviewedAtUtc?: string;
  applyingStartedAtUtc?: string;
  applyFailedAtUtc?: string;
  applyFailureCode?: string;
  applyFailureMessage?: string;
  requiresSellerAttention: boolean;
  statusLabel: string;
  statusReason: string;
  nextAction: string;
  changedAreas: string[];
  appliedImmediatelyAreas: string[];
  pendingModerationAreas: string[];
  capabilities: {
    canOpen: boolean;
    canMutate: boolean;
    canSubmit: boolean;
    canRetry: boolean;
    requiresReopenConfirmation: boolean;
  };
}

export interface SellerProductTranslationViewDto {
  languageCode: string;
  name: string;
  shortDescription: string;
  longDescription: string;
}

export interface SellerProductMediaViewDto {
  url: string;
  isVideo: boolean;
  sortOrder: number;
}

export interface SellerProductTagViewDto {
  id: string;
  name: string;
}

export interface SellerProductSpecificationTranslationViewDto {
  languageCode: string;
  title: string;
  value: string;
}

export interface SellerProductSpecificationViewDto {
  sortOrder: number;
  translations: SellerProductSpecificationTranslationViewDto[];
}

export interface SellerProductVariantOptionViewDto {
  attributeDefinitionId: string;
  attributeDefinitionName?: string;
  attributeValueId: string;
  attributeValueName?: string;
}

export interface SellerProductVariantViewDto {
  id: string;
  variantSku: string;
  regularPrice: number;
  regularPriceCurrency?: string;
  onSale: boolean;
  salePrice?: number;
  salePriceCurrency?: string;
  saleStartUtc?: string;
  saleEndUtc?: string;
  isActive: boolean;
  weightKg?: number;
  lengthCm?: number;
  widthCm?: number;
  heightCm?: number;
  processingTimeDays?: number;
  defaultPickupWarehouseId?: string;
  availableQuantity: number;
  inventory: SellerProductInventoryViewDto[];
  options: SellerProductVariantOptionViewDto[];
  originalRegularPrice?: number;
  originalRegularPriceCurrency?: string;
  originalSalePrice?: number;
  originalSalePriceCurrency?: string;
}

export interface SellerProductInventoryViewDto {
  id: string;
  warehouseId: string;
  quantityOnHand: number;
  reservedQuantity: number;
  availableQuantity: number;
  reorderLevel: number;
}

export interface SellerProductDetailsDto {
  id: string;
  sellerId: string;
  sellerName?: string;
  categoryId: string;
  categoryName?: string;
  categorySlug?: string;
  brandId?: string;
  brandName?: string;
  productType: string;
  status: string;
  mainImageUrl?: string;
  translations: SellerProductTranslationViewDto[];
  media: SellerProductMediaViewDto[];
  tags: SellerProductTagViewDto[];
  tagIds?: string[];
  specifications: SellerProductSpecificationViewDto[];
  variants: SellerProductVariantViewDto[];
  specialHandling?: ProductSpecialHandlingDto | null;
  totalAvailableQuantity: number;
  minRegularPrice: number;
  minSalePrice?: number;
}
